import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  TextField,
  Button,
  DataTable,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

export default function InventoryUsage() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'inventory-usage', startDate, endDate],
    queryFn: () => reportsApi.getInventoryUsage({ startDate, endDate }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/inventory-usage', { startDate, endDate });
    } finally {
      setExporting(false);
    }
  };

  // API returns data as a flat array of inventory objects
  const items = Array.isArray(data?.data) ? data.data : [];

  const rows =
    items.map((item) => [
      item.sku,
      item.productName,
      item.totalUnits,
      item.available,
      item.inUse,
      `${(Number(item.utilizationRate) * 100).toFixed(1)}%`,
      item.dispatchesInPeriod,
    ]);

  return (
    <Page
      title="Inventory Usage"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Inventory Usage report. Try refreshing.</p>
            </EmptyState>
          ) : !data ? (
            <EmptyState heading="No data" image="">
              <p>No inventory data found for the selected date range.</p>
            </EmptyState>
          ) : (
            <DataTable
              columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'numeric']}
              headings={['SKU', 'Product', 'Total', 'Available', 'In Use', 'Utilization %', 'Dispatches']}
              rows={rows}
              footerContent={rows.length === 0 ? 'No records found' : undefined}
            />
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
